import { TranslateFunc } from '@prisjakt/ui-translate/src/translate-func';
import { Maybe, Price, PriceList } from '../../../../../types';
import { IFilterState, ProductPriceType } from '../../../types';
import { IPriceFilter } from '../types';
import { SortOption, SortOptionEnum } from '../../sort-options/types';
import { sortByPrice } from '../../sort-options/utils';

export const getFilter = (prices?: Maybe<PriceList>) => {
  const initialFilter = {
    hasInternationalPrices: false,
    hasUsedProducts: false,
    hasNotInMintCondition: false,
    hasPickupOnly: false,
    hasUnavailable: false,
    hasFilter: false,
  };
  if (!prices || !prices.nodes) {
    return initialFilter;
  }

  const hasInternationalPrices = prices.nodes.some(
    p => p && p.store && p.store.market !== 'primary'
  );
  const hasUsedProducts = prices.nodes.some(
    p => p && p.type === ProductPriceType.USED_PRODUCT
  );
  const hasNotInMintCondition = prices.nodes.some(
    p => p && p.type === ProductPriceType.NOT_IN_MINT_CONDITION
  );
  const hasPickupOnly = prices.nodes.some(
    p => p && p.type === ProductPriceType.PICKUP_ONLY
  );
  const hasUnavailable = prices.nodes.some(
    p => p && p.type === ProductPriceType.UNAVAILABLE
  );

  return {
    hasInternationalPrices,
    hasUsedProducts,
    hasNotInMintCondition,
    hasPickupOnly,
    hasUnavailable,
  };
};

export const getValidPriceType = (type?: string | null) => {
  if (
    !type ||
    !Object.values(ProductPriceType).includes(type as ProductPriceType)
  )
    return null;

  return type as ProductPriceType;
};

export const getPriceFilters = (
  prices: Maybe<PriceList> | undefined,
  t: TranslateFunc,
  junkFilter: IFilterState,
  featuredAlternativePriceType: string | undefined,
  hiddenListOptions: string[]
): IPriceFilter[] => {
  const {
    hasInternationalPrices,
    hasUsedProducts,
    hasNotInMintCondition,
    hasPickupOnly,
    hasUnavailable,
  } = getFilter(prices);

  return [
    {
      name: 'showInternational',
      type: ProductPriceType.INTERNATIONAL,
      checked: junkFilter.showInternational,
      hasPrices: hasInternationalPrices,
      showAsQuickFilter:
        featuredAlternativePriceType === ProductPriceType.INTERNATIONAL,
      label: t('product', 'price-filter.international'),
      shortLabel: t('product', 'price-filter.international-short'),
      hide: false,
    },
    {
      name: 'showUsedProducts',
      type: ProductPriceType.USED_PRODUCT,
      checked: junkFilter.showUsedProducts,
      hasPrices: hasUsedProducts,
      showAsQuickFilter:
        featuredAlternativePriceType === ProductPriceType.USED_PRODUCT,
      label: t('product', 'price-filter.used'),
      hide: false,
    },
    {
      name: 'showNotInMintCondition',
      type: ProductPriceType.NOT_IN_MINT_CONDITION,
      checked: junkFilter.showNotInMintCondition,
      label: t('product', 'price-filter.not-in-mint-condition'),
      hasPrices: hasNotInMintCondition,
      showAsQuickFilter: false,
      hide: Boolean(
        hiddenListOptions.filter(
          option => option === ProductPriceType.NOT_IN_MINT_CONDITION
        ).length
      ),
    },
    {
      name: 'showPickupOnly',
      type: ProductPriceType.PICKUP_ONLY,
      checked: junkFilter.showPickupOnly,
      label: t('product', 'price-filter.pickup-only'),
      hasPrices: hasPickupOnly,
      showAsQuickFilter: false,
      hide: Boolean(
        hiddenListOptions.filter(
          option => option === ProductPriceType.PICKUP_ONLY
        ).length
      ),
    },
    {
      name: 'showUnavailable',
      type: ProductPriceType.UNAVAILABLE,
      checked: junkFilter.showUnavailable,
      label: t('product', 'price-filter.not-available-for-purchase'),
      hasPrices: hasUnavailable,
      showAsQuickFilter: false,
      hide: false,
    },
  ];
};

export const getSortedPrices = (
  currentSort: SortOption,
  prices?: Maybe<PriceList>
): Price[] | undefined | null => {
  const allPrices = prices?.nodes?.filter(x => x) as Price[] | null | undefined;

  if (
    allPrices?.length &&
    currentSort === SortOptionEnum.PRICE_INCLUDES_SHIPPING
  ) {
    return sortByPrice(currentSort, allPrices);
  }

  return allPrices;
};
